import { useEffect } from 'react';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import { GetStaticProps } from 'next';
import { ParsedUrlQuery } from 'querystring';
import Cookies from 'js-cookie';

/* Components */
import { getLayout } from '@components/PageLayouts/Layout/Standard';

/* Apis */
import { landings, skus } from '@api/apis';

/* Constants */
import { PAGE_TYPE_LANDING } from '@constants/pages';
import { SEVEN_DAYS } from '@constants/CacheRevalidate';
import { LandingPageTemplateTypes } from '@scenes/LandingsPage/landingspage.constants';
import { findCustomPartnerAndMerge } from '@integrations/partners/partners.utils';
import { PHONE_TRACKING_TYPES } from '@utils/cookies/phone';
import { EXPIRY_14_DAYS } from '@utils/cookies/cookies.constants';
import { LAYOUT_PAGE_TYPES } from '@components/PageLayouts/pagelayouts.constants';

/* Types */
import { Children } from 'types/base.types';
import { TLandings, TLandingSku } from 'types/landings.types';
import { PartnerBaseAttributes } from '@integrations/partners/partners.types';
import { SegmentTrackingAction } from '@integrations/segment/sku';
import { LANDINGS_FEATURED_SERVICES } from '@integrations/partners/partners.constants';
import { Sku } from 'types/sku.types';

/* Performance Dynamic */
const LandingPagesProductMultipleSku = dynamic(() => import('@scenes/LandingsPage/LandingPages.ProductMultipleSku'));
const LandingPagesProductSingleSku = dynamic(() => import('@scenes/LandingsPage/LandingPages.ProductSinglesku'));
const LandingPagesProductSinglePartnerSku = dynamic(() => import('@scenes/LandingsPage/LandingPages.ProductSinglesku.Partner'));
const LandingPagesAccountMultipleSku = dynamic(() => import('@scenes/LandingsPage/LandingPages.AccountMultipleSku'));

/*
 * Our base Services Sku Page. The page proper is broken out just in case we ever need to
 * house that container in another layout.
 * */
type TLandingPageProps = {
  landing: TLandings;
  partner: PartnerBaseAttributes;
};

function LandingPage(props: TLandingPageProps): Children {
  const router = useRouter();
  if (router.isReady) {
    /* match the template types */
    switch (props.landing.template) {
      case LandingPageTemplateTypes.InternalProductMultipleSku:
      case LandingPageTemplateTypes.ProductMultipleSku:
        return <LandingPagesProductMultipleSku {...props} />;
      case LandingPageTemplateTypes.ProductSingleSku:
      case LandingPageTemplateTypes.InternalGoogleAds:
      case LandingPageTemplateTypes.InternalProductSingleSku:
      case LandingPageTemplateTypes.GoogleAds: {
        if (props?.partner?.id) {
          return <LandingPagesProductSinglePartnerSku {...props} />;
        }
        return <LandingPagesProductSingleSku {...props} />;
      }
      case LandingPageTemplateTypes.AccountMultipleSku:
      case LandingPageTemplateTypes.InternalAccountMultipleSku:
      default:
        return <LandingPagesAccountMultipleSku {...props} />;
    }
  }

  /**
   * Set the cookie and fire segment
   */
  useEffect(() => {
    if (props.landing) {
      /* Handle Segment */
      SegmentTrackingAction.skuPageLanding(props.landing);
    }

    if (props.landing.phone) {
      Cookies.set(PHONE_TRACKING_TYPES.LANDING_PHONE, props.landing.phone, { expires: EXPIRY_14_DAYS });
    } else if (Cookies.get(PHONE_TRACKING_TYPES.LANDING_PHONE)) {
      Cookies.remove(PHONE_TRACKING_TYPES.LANDING_PHONE);
    }
  }, [props.landing]);

  return null;
}

LandingPage.pageAttributes = { page: PAGE_TYPE_LANDING };
LandingPage.Layout = getLayout({ promo: true, footer: true });

export default LandingPage;

/**
 * ---------------------------------------------------------------------------------------
 * Static Generation
 * @param params
 * ---------------------------------------------------------------------------------------
 */
interface StaticParams extends ParsedUrlQuery {
  seoName: string;
}

/**
 * - Get sku data
 * - Get reviews for sku
 * - Get routes for navigation (predone for rank/column)
 *
 * @param {GetStaticPropsContext<ParsedUrlQuery, PreviewData>} context
 * @returns {Promise<*>}
 */
export const getStaticProps: GetStaticProps = async context => {
  const params = context.params as StaticParams;
  const { data: { landing = '' } = {} } = await landings.find({ name: params.landingPage });
  const logo = landing?.logo || landing?.images?.logo;
  const isGoogleAds = landing?.template === LandingPageTemplateTypes.GoogleAds;
  const isGoogleAdsPartner = landing?.partner?.partnerName === 'Google Adwords';
  const isGoogleLanding = isGoogleAds || isGoogleAdsPartner;
  let validFeaturedServices: Partial<Sku>[] = [];
  /*
    Some partners want a "Featured Services" Tab.
    Get at it here and merge it with the landing data.
    At some point, they should be able to manage this by the BE.
   */
  const env = process.env.NEXT_PUBLIC_NODE_ENV as keyof typeof LANDINGS_FEATURED_SERVICES;
  if (
    (env || landing?.partner?.id) &&
    [
      LandingPageTemplateTypes.AccountMultipleSku,
      LandingPageTemplateTypes.MultipleAccountSku,
      LandingPageTemplateTypes.InternalAccountMultipleSku,
    ].includes(landing?.template)
  ) {
    const services = LANDINGS_FEATURED_SERVICES.get(env as string)?.get(landing.partner.id!);
    const mappedFetchedServices = (services || []).map(async (skuId: number) => {
      try {
        return await skus.find({ id: skuId });
      } catch (error) {
        return []; // Handle failed requests by returning null
      }
    });

    if (mappedFetchedServices.length) {
      const featuredServices = await Promise.allSettled(mappedFetchedServices);
      // Filter out any failed services if necessary
      validFeaturedServices = featuredServices
        .filter((result): result is PromiseFulfilledResult<any> => result.status === 'fulfilled' && result.value !== null)
        .map(result => {
          const sku = result.value?.data?.sku;

          return {
            id: sku.id,
            seoName: sku.seoName,
            name: sku.name,
            skuCategory: {
              id: 999,
              name: 'Featured Services',
            },
            hasAdjustments: sku.hasAdjustments || false,
            startsAtPrice: sku.startsAtPrice,
            partnerPrice: sku.partnerPrice,
            description: sku.description,
            skuBullets: sku.skuBullets,
            showFree: sku.showFree,
            ratingAttributes: sku.ratingAttributes,
            includedInUpsellPlan: sku.includedInUpsellPlan,
            image: {
              original: sku.heroImageUrl || 'missing',
              medium: sku.heroImageUrl || 'missing',
            },
          };
        });
    }

    // merge featured services
    landing.skus.push(...validFeaturedServices);
  }

  /* Remove excess data in skus.category.skus: If need it future, remove */
  if (landing?.skus) {
    landing.skus.forEach((sku: TLandingSku) => {
      if (sku.skuCategory && sku.skuCategory.skus) {
        delete sku.skuCategory.meta;
        delete sku.skuCategory.skus;
      }
    });
  }

  /* Single Sku Return, we need the full sku */
  if ([LandingPageTemplateTypes.ProductSingleSku, LandingPageTemplateTypes.GoogleAds].includes(landing.template)) {
    const { data: { sku = '' } = {} } = await skus.find({ id: landing.skus[0].seoName });
    landing.skus[0] = { ...landing.skus[0], ...sku };
  }

  if (!landing || landing.error) {
    throw new Error(`Landing Page '${params!.seoName}' not found`);
  }

  /* Lets merge our passed partner with a potentional custom/default partner */
  const partnerLandingData =
    !isGoogleLanding && landing.partner
      ? findCustomPartnerAndMerge(
          { ...landing.partner, logo, phone: landing.phone, homeUrl: `/landings/${landing.seoName}` },
          { mergedAttributes: { page: LAYOUT_PAGE_TYPES.PRECART } },
        )
      : null;

  return {
    props: {
      landing: { ...landing, ...(partnerLandingData && { partner: partnerLandingData }) },
      partner: partnerLandingData,
    },
    revalidate: SEVEN_DAYS,
  };
};
/**
 * -------------------------------------------------------------------------
 * Static Path Generation
 * - Get navigatable service skus
 * - Pluck out just the seoName for pathing for routes
 *
 * @returns {Promise<{paths: RouteSkuParams<string>[] | undefined; fallback: boolean}>}
 */
export async function getStaticPaths<GetStaticPaths>() {
  return {
    paths: [],
    fallback: 'blocking',
  };
}
